// ============================================================================
// BESPOKIAN Health - Terms of Service Page
// Legal page with luxury styling
// ============================================================================

import { Navigation } from '@/components/layout/Navigation';
import { Footer } from '@/components/layout/Footer';
import { FileText } from 'lucide-react';

const sections = [
  {
    title: '1. Acceptance of Terms',
    content: `By accessing or using BESPOKIAN Health services, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using our services.`,
  },
  {
    title: '2. Pharmacy Services',
    content: `BESPOKIAN Health is currently in the licensing phase and intends to be fully licensed to dispense in all 50 states. All medications are dispensed from facilities that meet strict state and federal regulatory standards, and every prescription is reviewed by a licensed pharmacist. We reserve the right to refuse to fill any prescription at our professional discretion.`,
  },
  {
    title: '3. Account Responsibilities',
    content: `You are responsible for maintaining the confidentiality of your account information and for all activities under your account. You must provide accurate, current, and complete information. Notify us immediately of any unauthorized use.`,
  },
  {
    title: '4. Pricing and Payment',
    content: `Our pricing is transparent: actual drug acquisition cost + 15% markup (capped at $300) + $5 dispensing fee. Prices may change without notice. Payment is due at the time of order. We accept major credit cards and HSA/FSA cards.`,
  },
  {
    title: '5. Shipping and Delivery',
    content: `Standard shipping is typically 2-3 business days. We plan to offer free shipping on orders over $50, with express next-day delivery options available. Delivery times are estimates and not guaranteed. Risk of loss passes to you upon delivery to the carrier. Where permitted by state and federal law, controlled substances require adult signature upon delivery.`,
  },
  {
    title: '6. Returns and Refunds',
    content: `Due to the nature of prescription medications, we cannot accept returns of dispensed medications. If you receive damaged or incorrect medication, contact us immediately. Refunds are issued at our discretion.`,
  },
  {
    title: '7. Telemedicine Services',
    content: `You may access integrated telemedicine services to consult with licensed providers who can evaluate and prescribe medications for appropriate conditions. BESPOKIAN Health facilitates these services but does not provide medical advice directly. Consultation fees are non-refundable once a provider has reviewed your case.`,
  },
  {
    title: '8. Privacy and HIPAA',
    content: `Your use of our services is also governed by our Privacy Policy and HIPAA Notice of Privacy Practices. We maintain rigorous HIPAA-compliant protocols to protect your personal health information in accordance with applicable laws.`,
  },
  {
    title: '9. Limitation of Liability',
    content: `BESPOKIAN Health shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our services. Our total liability shall not exceed the amount paid by you for the specific service giving rise to the claim.`,
  },
  {
    title: '10. Modifications',
    content: `We reserve the right to modify these terms at any time. Continued use of our services after changes constitutes acceptance of the modified terms. We will notify you of significant changes via email or website notice.`,
  },
  {
    title: '11. Governing Law',
    content: `These terms are governed by the laws of the State of Florida, without regard to conflict of law principles. Any disputes shall be resolved in the courts located in Seminole County, Florida.`,
  },
];

export function TermsPage() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navigation />

      <main className="flex-1 pt-24">
        {/* Hero */}
        <section className="bg-gradient-to-br from-navy-50 via-white to-gold-50/30 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />

            <p className="text-gold-600 font-medium tracking-wide uppercase text-sm mb-4">
              Legal
            </p>

            <h1 className="font-display text-4xl md:text-5xl text-navy-800 tracking-tight leading-tight mb-6 italic">
              Terms of Service
            </h1>

            <p className="text-xl text-navy-600 max-w-2xl mx-auto">
              Please read these terms carefully before using our services.
            </p>

            <p className="text-sm text-navy-400 mt-4">Last updated: January 2026</p>
          </div>
        </section>

        {/* Content */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-8">
              {sections.map((section, index) => (
                <div key={section.title} className={`pb-8 ${index !== sections.length - 1 ? 'border-b border-navy-100' : ''}`}>
                  <h2 className="text-lg font-semibold text-navy-800 mb-3">{section.title}</h2>
                  <p className="text-navy-600 leading-relaxed">{section.content}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 p-6 bg-navy-50 rounded-xl border border-navy-100">
              <div className="flex items-start gap-4">
                <FileText className="h-6 w-6 text-navy-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-navy-800 mb-2">Questions About These Terms?</h3>
                  <p className="text-navy-600">
                    Contact us at{' '}
                    <a href="mailto:legal@bespokianhealth.com" className="text-gold-600 hover:text-gold-700 font-medium">
                      legal@bespokianhealth.com
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}



